import { ArrowDown, ArrowUp, CalendarDays, Filter, Plus, Search, Tag as TagIcon, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { DateRangePicker } from '@/components/forms/DateRangePicker'
import { CategorizedTagOptions } from '@/components/tags/CategorizedTagOptions'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useTagCategories } from '@/hooks/useTagCategories'
import { useTags } from '@/hooks/useTags'
import type { FormField } from '@/types/api/form'
import type { RecordFieldOperator, RecordFilterMatch, RecordTagMode } from '@/types/api/record'
import type { Tag } from '@/types/api/tag'
import { RecordSourceType } from '@/types/enums'
import { cn } from '@/utils/cn'

import {
  SOURCE_LABELS,
  describeDateRange,
  describeFilter,
  fieldChoices,
  fieldKind,
  isComplete,
  isSortableField,
  operatorLabel,
  operatorNeedsValue,
  operatorsFor,
  type FieldKind,
  type RecordFilter,
} from '../utils/recordFilters'

import type { RecordSort } from './RecordsTable'

export interface RecordFilterState {
  q: string
  filters: RecordFilter[]
  match: RecordFilterMatch
  includeSubgroups: boolean
}

interface RecordFilterBarProps {
  recordGroupId: string
  /** Form fields offered as filters (the table's columns). */
  fields: FormField[]
  state: RecordFilterState
  onChange: (patch: Partial<RecordFilterState>) => void
  /** Shows the "Include sub-groups" switch. */
  hasSubgroups?: boolean
  sort: RecordSort
  onSortChange: (sort: RecordSort) => void
}

/** Chips for these kinds are replaced by the inline Tags and Created buttons. */
const INLINE_KINDS: RecordFilter['kind'][] = ['tags', 'created']

const BASE_SORTS = [
  { value: 'createdAt', label: 'Created' },
  { value: 'updatedAt', label: 'Updated' },
  { value: 'title', label: 'Title' },
]

function TagDot({ tag }: { tag: Pick<Tag, 'colorHex'> }) {
  return <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: tag.colorHex ?? 'var(--muted-foreground)' }} />
}

// ── editors ───────────────────────────────────────────────────────────────────

function SourceEditor({ filter, onChange }: { filter: Extract<RecordFilter, { kind: 'source' }>; onChange: (f: RecordFilter) => void }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Source is any of</p>
      {Object.values(RecordSourceType).map((source) => {
        const checked = filter.values.includes(source)
        return (
          <label key={source} className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={checked}
              onCheckedChange={(next) =>
                onChange({
                  ...filter,
                  values: next ? [...filter.values, source] : filter.values.filter((v) => v !== source),
                })
              }
            />
            {SOURCE_LABELS[source]}
          </label>
        )
      })}
    </div>
  )
}

function TagsEditor({
  filter,
  tags,
  categories,
  onChange,
}: {
  filter: Extract<RecordFilter, { kind: 'tags' }>
  tags: Tag[]
  categories: Parameters<typeof CategorizedTagOptions>[0]['categories']
  onChange: (f: RecordFilter) => void
}) {
  const selected = new Set(filter.ids)
  return (
    <div className="space-y-2">
      <Select value={filter.mode} onValueChange={(mode) => onChange({ ...filter, mode: mode as RecordTagMode })}>
        <SelectTrigger aria-label="Tag match mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ANY">Has any of</SelectItem>
          <SelectItem value="ALL">Has all of</SelectItem>
          <SelectItem value="NONE">Has none of</SelectItem>
        </SelectContent>
      </Select>
      <div className="max-h-60 space-y-1 overflow-y-auto">
        {tags.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">This group has no record tags yet.</p>
        ) : (
          <CategorizedTagOptions
            tags={tags}
            categories={categories}
            onPick={(tag) =>
              onChange({
                ...filter,
                ids: selected.has(tag.id) ? filter.ids.filter((id) => id !== tag.id) : [...filter.ids, tag.id],
              })
            }
            renderTag={(tag) => (
              <>
                <Checkbox checked={selected.has(tag.id)} tabIndex={-1} aria-hidden className="pointer-events-none" />
                <TagDot tag={tag} />
                <span className="truncate text-sm">{tag.label}</span>
              </>
            )}
          />
        )}
      </div>
    </div>
  )
}

function DateRangeEditor({ filter, onChange }: { filter: Extract<RecordFilter, { kind: 'created' | 'updated' }>; onChange: (f: RecordFilter) => void }) {
  return (
    <DateRangePicker
      idPrefix={filter.kind}
      from={filter.from}
      to={filter.to}
      onChange={(from, to) => onChange({ ...filter, from, to })}
    />
  )
}

function ValueInput({ kind, value, onChange, label }: { kind: FieldKind; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <Input
      aria-label={label}
      className="h-9"
      type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
      step={kind === 'number' ? 'any' : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function FieldEditor({
  filter,
  field,
  onChange,
}: {
  filter: Extract<RecordFilter, { kind: 'field' }>
  field: FormField | undefined
  onChange: (f: RecordFilter) => void
}) {
  const kind = field ? fieldKind(field.type) : 'text'
  const need = operatorNeedsValue(filter.op)
  const choices = field ? fieldChoices(field) : []
  const values = filter.values ?? []

  return (
    <div className="space-y-2">
      <p className="truncate text-xs font-medium text-muted-foreground">{field?.label ?? filter.key}</p>
      <Select
        value={filter.op}
        onValueChange={(op) => onChange({ ...filter, op: op as RecordFieldOperator, value: undefined, values: undefined })}
      >
        <SelectTrigger className="h-9" aria-label="Condition">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operatorsFor(kind).map((op) => (
            <SelectItem key={op} value={op}>
              {operatorLabel(op, kind)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {need === 'one' && kind === 'toggle' && (
        <Select value={filter.value ?? ''} onValueChange={(value) => onChange({ ...filter, value })}>
          <SelectTrigger className="h-9" aria-label="Value">
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      )}
      {need === 'one' && kind !== 'toggle' && (
        <ValueInput kind={kind} label="Value" value={filter.value ?? ''} onChange={(value) => onChange({ ...filter, value })} />
      )}
      {need === 'two' && kind === 'date' && (
        <DateRangePicker
          idPrefix={`field-${filter.key}`}
          from={values[0] || undefined}
          to={values[1] || undefined}
          onChange={(from, to) => onChange({ ...filter, values: [from ?? '', to ?? ''] })}
        />
      )}
      {need === 'two' && kind !== 'date' && (
        <div className="grid grid-cols-2 gap-2">
          <ValueInput kind={kind} label="From" value={values[0] ?? ''} onChange={(v) => onChange({ ...filter, values: [v, values[1] ?? ''] })} />
          <ValueInput kind={kind} label="To" value={values[1] ?? ''} onChange={(v) => onChange({ ...filter, values: [values[0] ?? '', v] })} />
        </div>
      )}
      {need === 'many' && (
        choices.length > 0 ? (
          <div className="max-h-52 space-y-1.5 overflow-y-auto">
            {choices.map((choice) => (
              <label key={choice} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={values.includes(choice)}
                  onCheckedChange={(next) =>
                    onChange({ ...filter, values: next ? [...values, choice] : values.filter((v) => v !== choice) })
                  }
                />
                <span className="truncate">{choice}</span>
              </label>
            ))}
          </div>
        ) : (
          <Input
            className="h-9"
            aria-label="Values, comma separated"
            placeholder="value1, value2"
            value={values.join(', ')}
            onChange={(e) => onChange({ ...filter, values: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })}
          />
        )
      )}
    </div>
  )
}

// ── bar ───────────────────────────────────────────────────────────────────────

/**
 * Search box plus stackable filter chips (source, tags, dates, "mine", any form field). Each
 * chip opens an editor popover; incomplete chips are shown dashed and are not applied.
 */
export function RecordFilterBar({ recordGroupId, fields, state, onChange, hasSubgroups = false, sort, onSortChange }: RecordFilterBarProps) {
  const { tags } = useTags(recordGroupId, 'RECORD')
  const { categories } = useTagCategories(recordGroupId, 'RECORD')
  const activeTags = useMemo(() => tags.filter((t) => !t.archived), [tags])
  const [searchInput, setSearchInput] = useState(state.q)
  const debounced = useDebouncedValue(searchInput, 300)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // Only push text typed here (see RecordGroupBrowser): URL changes from elsewhere are followed, not overwritten.
  const syncedQuery = useRef(state.q)
  useEffect(() => {
    if (debounced !== syncedQuery.current) {
      syncedQuery.current = debounced
      onChange({ q: debounced })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])
  useEffect(() => {
    syncedQuery.current = state.q
    setSearchInput(state.q)
  }, [state.q])

  const fieldsByKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields])
  const tagLabels = useMemo(() => new Map(tags.map((t) => [t.id, t.label])), [tags])
  const describeCtx = {
    fieldLabel: (key: string) => fieldsByKey.get(key)?.label ?? key,
    fieldKind: (key: string) => {
      const f = fieldsByKey.get(key)
      return f ? fieldKind(f.type) : 'text'
    },
    tagLabel: (id: string) => tagLabels.get(id) ?? 'Unknown tag',
  }

  const filters = state.filters
  const tagsFilter = filters.find((f): f is Extract<RecordFilter, { kind: 'tags' }> => f.kind === 'tags')
  const createdFilter = filters.find((f): f is Extract<RecordFilter, { kind: 'created' | 'updated' }> => f.kind === 'created')
  // Indices stay those of the full list; chips of the inline kinds are simply not drawn.
  const chips = filters.map((filter, index) => ({ filter, index })).filter(({ filter }) => !INLINE_KINDS.includes(filter.kind))
  const sortOptions = useMemo(
    () => [
      ...BASE_SORTS,
      ...fields.filter(isSortableField).map((f) => ({ value: `field:${f.key}`, label: f.label })),
    ],
    [fields]
  )
  const combinable = filters.filter((f) => (f.kind === 'tags' || f.kind === 'field') && isComplete(f)).length
  const activeCount = filters.filter(isComplete).length + (state.q ? 1 : 0)

  const addFilter = (filter: RecordFilter) => {
    onChange({ filters: [...filters, filter] })
    setOpenIndex(filters.length)
  }
  const updateFilter = (index: number, filter: RecordFilter) =>
    onChange({ filters: filters.map((f, i) => (i === index ? filter : f)) })
  const removeFilter = (index: number) => {
    onChange({ filters: filters.filter((_, i) => i !== index) })
    setOpenIndex(null)
  }
  /** Sets, replaces or (with null) removes the single filter of an inline kind. */
  const setInline = (kind: 'tags' | 'created', next: RecordFilter | null) => {
    const rest = filters.filter((f) => f.kind !== kind)
    onChange({ filters: next ? [...rest, next] : rest })
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[12rem] flex-1 basis-full sm:basis-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search records…"
            aria-label="Search records"
            className="pl-10 focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0"
          />
        </label>

        <div className="flex max-w-full items-center gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible">
          <Popover
            onOpenChange={(open) => {
              // An empty tags chip (only a mode picked) is dropped when the editor closes.
              if (!open && tagsFilter && tagsFilter.ids.length === 0) setInline('tags', null)
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn('h-10 shrink-0', tagsFilter && isComplete(tagsFilter) && 'border-primary/40 bg-primary/5')}
              >
                <TagIcon />
                {tagsFilter && isComplete(tagsFilter) ? `Tags · ${tagsFilter.ids.length}` : 'Tags'}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72">
              <TagsEditor
                filter={tagsFilter ?? { kind: 'tags', ids: [], mode: 'ANY' }}
                tags={activeTags}
                categories={categories}
                onChange={(f) => {
                  if (f.kind !== 'tags') return
                  setInline('tags', f.ids.length === 0 && f.mode === 'ANY' ? null : f)
                }}
              />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn('h-10 shrink-0', createdFilter && isComplete(createdFilter) && 'border-primary/40 bg-primary/5')}
              >
                <CalendarDays />
                {createdFilter && isComplete(createdFilter)
                  ? `Created · ${describeDateRange(createdFilter.from, createdFilter.to)}`
                  : 'Created'}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72">
              <DateRangePicker
                idPrefix="created"
                from={createdFilter?.from}
                to={createdFilter?.to}
                onChange={(from, to) => setInline('created', from || to ? { kind: 'created', from, to } : null)}
              />
            </PopoverContent>
          </Popover>

          <div className="flex shrink-0 items-center gap-1">
            <Select value={sort.by} onValueChange={(by) => onSortChange({ by, dir: sort.dir })}>
              <SelectTrigger className="h-10 w-auto min-w-[8.5rem] gap-2" aria-label="Sort by">
                <span className="text-muted-foreground">Sort</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10"
              aria-label={sort.dir === 'ASC' ? 'Sorted ascending, switch to descending' : 'Sorted descending, switch to ascending'}
              onClick={() => onSortChange({ by: sort.by, dir: sort.dir === 'ASC' ? 'DESC' : 'ASC' })}
            >
              {sort.dir === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className="h-10">
                <Filter />
                Add filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-80 w-56 overflow-y-auto">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Record</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => addFilter({ kind: 'source', values: [] })}>Source</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => addFilter({ kind: 'updated' })}>Updated date</DropdownMenuItem>
              <DropdownMenuItem
                disabled={filters.some((f) => f.kind === 'mine')}
                onSelect={() => addFilter({ kind: 'mine' })}
              >
                Created by me
              </DropdownMenuItem>
              {fields.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Fields</DropdownMenuLabel>
                  {fields.map((field) => (
                    <DropdownMenuItem
                      key={field.key}
                      onSelect={() => addFilter({ kind: 'field', key: field.key, op: operatorsFor(fieldKind(field.type))[0] })}
                    >
                      <span className="truncate">{field.label}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {hasSubgroups && (
            <label className="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm">
              <Switch
                checked={state.includeSubgroups}
                onCheckedChange={(includeSubgroups) => onChange({ includeSubgroups })}
                aria-label="Include records of sub-groups"
              />
              Sub-groups
            </label>
          )}
        </div>
      </div>

      {(chips.length > 0 || activeCount > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {combinable > 1 && (
            <div className="flex items-center rounded-md border p-0.5 text-xs" role="group" aria-label="Combine tag and field filters">
              {(['ALL', 'ANY'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={state.match === m}
                  onClick={() => onChange({ match: m })}
                  className={cn(
                    'rounded px-2 py-1 font-medium transition-colors',
                    state.match === m ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {m === 'ALL' ? 'Match all' : 'Match any'}
                </button>
              ))}
            </div>
          )}
          {chips.map(({ filter, index }) => {
            const complete = isComplete(filter)
            const field = filter.kind === 'field' ? fieldsByKey.get(filter.key) : undefined
            return (
              <Popover
                key={index}
                open={openIndex === index}
                onOpenChange={(open) => setOpenIndex(open ? index : null)}
              >
                <div
                  className={cn(
                    'inline-flex max-w-full items-center rounded-full border bg-background text-xs',
                    complete ? 'border-primary/40 bg-primary/5' : 'border-dashed text-muted-foreground'
                  )}
                >
                  <PopoverTrigger asChild>
                    <button type="button" className="max-w-[18rem] truncate py-1 pl-3 pr-1 font-medium">
                      {describeFilter(filter, describeCtx)}
                    </button>
                  </PopoverTrigger>
                  <button
                    type="button"
                    aria-label="Remove filter"
                    onClick={() => removeFilter(index)}
                    className="mr-1 rounded-full p-1 hover:bg-muted"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <PopoverContent className="w-72">
                  {filter.kind === 'source' && <SourceEditor filter={filter} onChange={(f) => updateFilter(index, f)} />}
                  {filter.kind === 'updated' && (
                    <DateRangeEditor filter={filter} onChange={(f) => updateFilter(index, f)} />
                  )}
                  {filter.kind === 'mine' && (
                    <p className="text-sm text-muted-foreground">Only records you created.</p>
                  )}
                  {filter.kind === 'field' && (
                    <FieldEditor filter={filter} field={field} onChange={(f) => updateFilter(index, f)} />
                  )}
                </PopoverContent>
              </Popover>
            )
          })}
          {activeCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('')
                onChange({ q: '', filters: [], match: 'ALL' })
              }}
            >
              Clear all
            </Button>
          )}
          {filters.length === 0 && !state.q && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Plus className="h-3 w-3" /> Add a filter to narrow the records
            </span>
          )}
        </div>
      )}
    </div>
  )
}
